// ── Apogee Query API Types ──────────────────────────────────────────────────
// Full type coverage for every schema in the Apogee OpenAPI spec.
// Upstream MPI docs: https://www.modpackindex.com/api

// ── Bucket & Sort Types ─────────────────────────────────────────────────────

export type TimeseriesBucket = 'raw' | '1h' | '1d';

/** Sort field; prefix `-` for descending. */
export type SortDirection = string;

/** Apogee multi-window activity index (`pack_popularity_scores`); aligns with server window labels. */
export const APOGEE_ACTIVITY_WINDOWS = ['7d', '30d', '90d', '180d', '365d'] as const;
export type ApogeeActivityWindow = (typeof APOGEE_ACTIVITY_WINDOWS)[number];

function activitySortFieldsForWindows(): string[] {
  const out: string[] = [];
  for (const w of APOGEE_ACTIVITY_WINDOWS) {
    out.push(`activity_score_${w}`, `activity_rank_${w}`);
  }
  return out;
}

export const PACK_LIST_SORT_FIELDS = [
  'mpi_id',
  'apogee_id',
  'slug',
  'name',
  'summary',
  'first_seen_at',
  'first_seen_download_count',
  'download_count',
  'popularity_rank',
  ...activitySortFieldsForWindows(),
  'latest_release_date',
  'first_release_at',
  'last_modified',
  'last_updated',
  'snapshot_observed_at',
] as const;

export type PackListSortField = (typeof PACK_LIST_SORT_FIELDS)[number];

/** Subset exposed in the modpack browser catalog sort UI (maps to `GET /v1/packs` `sort`). */
export const PACK_BROWSER_CATALOG_SORT_PRESETS = [
  { value: '-download_count', label: 'Most downloads' },
  /** Lower `popularity_rank` is better (#1 first). */
  { value: 'popularity_rank', label: 'Highest rank' },
  { value: '-last_updated', label: 'Recently updated' },
  { value: '-first_seen_at', label: 'Newest on index' },
  { value: 'name', label: 'Name A–Z' },
] as const;

export const RECENT_UPDATES_SORT_FIELDS = [
  'observed_at',
  'last_updated',
  'mpi_id',
  'apogee_id',
  'slug',
  'name',
] as const;

export const DOWNLOAD_GROWTH_SORT_FIELDS = [
  'delta_downloads',
  'download_count_latest',
  'download_count_baseline',
  'mpi_id',
  'apogee_id',
  'slug',
  'name',
] as const;

export const EMERGING_SORT_FIELDS = [
  'delta_downloads',
  'download_count_latest',
  'download_count_baseline',
  'first_seen_at',
  'first_seen_download_count',
  'mpi_id',
  'apogee_id',
  'slug',
  'name',
] as const;

// ── Error ───────────────────────────────────────────────────────────────────

export interface ApogeeApiError {
  error: {
    code: string;
    message: string;
  };
}

/** Thrown when `GET /v1/search/*` returns 404 (Elasticsearch not configured). */
export class ApogeeSearchDisabledError extends Error {
  constructor(message = 'Search is not configured on this deployment.') {
    super(message);
    this.name = 'ApogeeSearchDisabledError';
  }
}

// ── Health ──────────────────────────────────────────────────────────────────

export interface ReadyResponse {
  status: string;
}

// ── Pack enrichment (Modrinth / CurseForge ingest) — referenced by pack rows ─

export interface ModrinthEnrichment {
  project_id: string;
  slug: string;
  name: string;
  author?: string;
  description?: string;
  observed_at: string;
}

export interface CurseForgeEnrichment {
  cf_mod_id: number;
  author?: string;
  description?: string;
  observed_at: string;
}

/** CurseForge featured carousel presence / span hints from `curseforge_featured_spans`. */
export interface CurseForgeFeaturedInfo {
  currently_featured: boolean;
  cf_mod_id?: number;
  since?: string;
  last_seen_at?: string;
}

export interface ApogeeActivityWindowMetrics {
  score: number;
  rank: number;
}

/** Multi-window activity score from `pack_popularity_scores`; null when never computed. */
export interface ApogeeActivity {
  computed_at: string;
  windows: Partial<Record<ApogeeActivityWindow, ApogeeActivityWindowMetrics>>;
}

export interface PackEnrichmentResponse {
  mpi_id: number | null;
  modrinth: ModrinthEnrichment | null;
  curseforge: CurseForgeEnrichment | null;
}

// ── Pack Core ───────────────────────────────────────────────────────────────

export interface PackRow {
  apogee_id: number;
  /** Modpack Index id; nullable under CF-primary identity (null when not matched to MPI). */
  mpi_id: number | null;
  /** CurseForge project id (CF-primary external key); omitted/null when not mapped. */
  cf_mod_id?: number | null;
  slug: string;
  name: string;
  summary: string;
  thumbnail_url: string | null;
  first_seen_at: string;
  first_seen_download_count: number;
  /** Present when a rollup row exists in `mv_modpack_latest`; omitted otherwise. */
  download_count?: number;
  /** Download-based catalog rank from rollup; omitted when no rollup row. */
  popularity_rank?: number | null;
  latest_release_date: string | null;
  last_modified: string | null;
  last_updated: string | null;
  /** Present when rollup metrics exist; omitted otherwise. */
  snapshot_observed_at?: string;
  /** Earliest release date MPI has ever reported — proxy for "originally published". */
  first_release_at?: string | null;
  /** Canonical Modpack Index page URL. */
  modpack_index_page_url?: string | null;
  /** Primary external listing URL (e.g. CurseForge project page). */
  external_page_url?: string | null;
  /** Named outbound links from MPI (e.g. { curseforge: "https://..." }). */
  links?: Record<string, string> | null;
  /** Modrinth ingest snapshot when enriched; omitted on older API responses. */
  modrinth?: ModrinthEnrichment | null;
  /** CurseForge ingest snapshot when enriched; omitted on older API responses. */
  curseforge?: CurseForgeEnrichment | null;
  /** CurseForge featured-list tracking; omitted when unknown or not mapped to a CF project. */
  curseforge_featured?: CurseForgeFeaturedInfo | null;
  /** Apogee activity index; JSON null from API becomes null here. */
  apogee_activity?: ApogeeActivity | null;
}

export interface PackListResponse {
  items: PackRow[];
  total: number;
  limit: number;
  offset: number;
  sort: string;
}

// ── Search (GET /v1/search/packs) ───────────────────────────────────────────

/** Flat document from the pack search index (`hits[]. _source`). */
export interface PackSearchDocument {
  apogee_id?: number;
  mpi_id?: number;
  slug?: string;
  name?: string;
  summary?: string;
  thumbnail_url?: string | null;
  mpi_page_url?: string | null;
  mpi_external_url?: string | null;
  modrinth_project_id?: string;
  modrinth_slug?: string;
  modrinth_name?: string;
  modrinth_author?: string;
  modrinth_description?: string;
  modrinth_observed_at?: string;
  curseforge_mod_id?: number;
  curseforge_author?: string;
  curseforge_description?: string;
  curseforge_observed_at?: string;
  download_count?: number;
  popularity_rank?: number | null;
  first_seen_at?: string;
  first_seen_download_count?: number;
  latest_release_date?: string | null;
  last_modified?: string | null;
  last_updated?: string | null;
  snapshot_observed_at?: string;
  first_release_at?: string | null;
  minecraft_versions?: string[];
  [key: string]: unknown;
}

export interface PackSearchHit {
  _id: string;
  _score: number | null;
  _source: PackSearchDocument;
}

export interface PackSearchResponse {
  q: string;
  total: number;
  limit: number;
  offset: number;
  hits: PackSearchHit[];
}

export interface SearchPacksParams {
  q: string;
  limit?: number;
  offset?: number;
}

// ── Modlist Hash Lookup ─────────────────────────────────────────────────────

export interface ModlistHashLookupRow {
  apogee_id: number;
  mpi_id: number | null;
  cf_mod_id?: number | null;
  slug: string;
  name: string;
  summary: string;
  thumbnail_url: string | null;
  first_seen_at: string;
  first_seen_download_count: number;
  modlist_snapshot_id: number;
  modlist_matched_observed_at: string;
  matches_latest_ingested_modlist: boolean;
  current_modlist_hash: string | null;
  current_modlist_observed_at: string | null;
  download_count?: number;
  popularity_rank?: number | null;
  snapshot_observed_at: string | null;
  /** MPI last_updated from the rollup — now included on hash lookups. */
  last_updated?: string | null;
  /** Earliest release date MPI has ever reported. */
  first_release_at?: string | null;
  /** Canonical Modpack Index page URL. */
  modpack_index_page_url?: string | null;
  /** Primary external listing URL (e.g. CurseForge project page). */
  external_page_url?: string | null;
  /** Named outbound links from MPI. */
  links?: Record<string, string> | null;
  modrinth?: ModrinthEnrichment | null;
  curseforge?: CurseForgeEnrichment | null;
  curseforge_featured?: CurseForgeFeaturedInfo | null;
  apogee_activity?: ApogeeActivity | null;
}

export interface ModlistHashLookupResponse {
  modlist_hash: string;
  total: number;
  sort: string;
  items: ModlistHashLookupRow[];
}

// ── CF Modlist Hash Lookup ──────────────────────────────────────────────────

export interface CFModlistHashLookupRow {
  apogee_id: number;
  mpi_id: number | null;
  cf_mod_id?: number | null;
  slug: string;
  name: string;
  summary: string;
  thumbnail_url: string | null;
  first_seen_at: string;
  first_seen_download_count: number;
  modlist_snapshot_id: number;
  cf_modlist_matched_observed_at: string;
  matches_latest_resolved_cf_modlist: boolean;
  latest_resolved_cf_modlist_hash: string | null;
  current_modlist_hash: string | null;
  current_modlist_observed_at: string | null;
  download_count?: number;
  popularity_rank?: number | null;
  snapshot_observed_at: string | null;
  /** MPI last_updated from the rollup — now included on hash lookups. */
  last_updated?: string | null;
  /** Earliest release date MPI has ever reported. */
  first_release_at?: string | null;
  /** Canonical Modpack Index page URL. */
  modpack_index_page_url?: string | null;
  /** Primary external listing URL (e.g. CurseForge project page). */
  external_page_url?: string | null;
  /** Named outbound links from MPI. */
  links?: Record<string, string> | null;
  modrinth?: ModrinthEnrichment | null;
  curseforge?: CurseForgeEnrichment | null;
  curseforge_featured?: CurseForgeFeaturedInfo | null;
  apogee_activity?: ApogeeActivity | null;
}

export interface CFModlistHashLookupResponse {
  cf_modlist_hash: string;
  total: number;
  sort: string;
  items: CFModlistHashLookupRow[];
}

// ── Snapshots ───────────────────────────────────────────────────────────────

export interface SnapshotRow {
  observed_at: string;
  download_count?: number;
  popularity_rank: number | null;
  latest_release_date: string | null;
  last_modified: string | null;
  last_updated: string | null;
  source: string;
}

export interface SnapshotListResponse {
  items: SnapshotRow[];
}

// ── Timeseries ──────────────────────────────────────────────────────────────

export interface PackTimeseriesPoint {
  t: string;
  download_count: number;
  popularity_rank: number | null;
}

export interface PackTimeseriesResponse {
  apogee_id: number;
  mpi_id: number | null;
  bucket: string;
  from: string;
  to: string;
  points: PackTimeseriesPoint[];
}

// ── Modlists ────────────────────────────────────────────────────────────────

export interface ModlistRow {
  snapshot_id: number;
  observed_at: string;
  mod_ids: number[];
  modlist_hash: string;
  cf_project_ids?: number[];
  cf_modlist_hash: string | null;
  cf_modlist_complete: boolean;
}

export interface ModlistListResponse {
  items: ModlistRow[];
}

// ── Modlist Diff ────────────────────────────────────────────────────────────

export interface ModlistDiffResponse {
  mpi_id: number | null;
  from_observed_at: string;
  to_observed_at: string;
  from_snapshot_id: number | null;
  to_snapshot_id: number | null;
  mods_added: number[];
  mods_removed: number[];
  from_count: number;
  to_count: number;
}

// ── External IDs ────────────────────────────────────────────────────────────

export interface ExternalIdRow {
  source: string;
  external_id: string;
  confidence: number | null;
  forced: boolean;
  updated_at: string;
}

export interface ExternalIdListResponse {
  items: ExternalIdRow[];
}

// ── CurseForge Files ────────────────────────────────────────────────────────

export interface CurseForgeFileRow {
  cf_mod_id: number;
  cf_file_id: number;
  display_name: string;
  file_date: string | null;
  download_count: number;
  release_type: number | null;
  observed_at: string;
}

export interface CurseForgeFileListResponse {
  items: CurseForgeFileRow[];
}

// ── CurseForge featured history ─────────────────────────────────────────────

export interface CurseForgeFeaturedSpanRow {
  started_at: string;
  ended_at: string | null;
  last_seen_at: string;
  cf_mod_id: number;
}

export interface CurseForgeFeaturedHistoryResponse {
  mpi_id: number | null;
  spans: CurseForgeFeaturedSpanRow[];
  latest_crawl_observed_at: string | null;
}

// ── CurseForge per-file download metric time series ─────────────────────────

export interface CurseForgeFileMetricPointRow {
  observed_at: string;
  mpi_id: number | null;
  cf_mod_id: number;
  cf_file_id: number;
  download_count: number;
}

export interface CurseForgeFileMetricPointsResponse {
  items: CurseForgeFileMetricPointRow[];
}

// ── Changelogs ──────────────────────────────────────────────────────────────

export interface ChangelogRow {
  cf_mod_id: number;
  cf_file_id: number;
  observed_at: string;
  body_html_excerpt: string;
  has_spilled_to_disk: boolean;
  body_html_full?: string;
}

export interface ChangelogListResponse {
  items: ChangelogRow[];
}

// ── Insights: Recent Updates ────────────────────────────────────────────────

export interface RecentUpdateRow {
  apogee_id: number;
  mpi_id: number | null;
  cf_mod_id?: number | null;
  name: string;
  slug: string;
  last_updated: string | null;
  observed_at: string;
}

export interface RecentUpdatesResponse {
  items: RecentUpdateRow[];
  since_hours: number;
  sort: string;
}

// ── Insights: Download Growth ───────────────────────────────────────────────

export interface DownloadGrowthRow {
  apogee_id: number;
  mpi_id: number | null;
  cf_mod_id?: number | null;
  name: string;
  slug: string;
  download_count_latest: number;
  download_count_baseline: number;
  delta_downloads: number;
}

export interface DownloadGrowthResponse {
  items: DownloadGrowthRow[];
  window_days: number;
  sort: string;
}

// ── Insights: Emerging ──────────────────────────────────────────────────────

export interface EmergingRow {
  apogee_id: number;
  mpi_id: number | null;
  cf_mod_id?: number | null;
  name: string;
  slug: string;
  first_seen_at: string;
  first_seen_download_count: number;
  download_count_baseline: number;
  download_count_latest: number;
  delta_downloads: number;
  growth_factor_vs_baseline: number | null | undefined;
}

export interface EmergingResponse {
  items: EmergingRow[];
  first_seen_within_days: number;
  min_delta_downloads: number;
  sort: string;
}

// ── Request Parameter Types ─────────────────────────────────────────────────

export interface ListPacksParams {
  limit?: number;
  offset?: number;
  sort?: string;
}

/** Max `limit` for `GET /v1/search/packs` per Apogee API. */
export const SEARCH_PACKS_MAX_LIMIT = 100;
/** Max `offset` for search pagination per Apogee API. */
export const SEARCH_PACKS_MAX_OFFSET = 10000;

export interface TimeseriesParams {
  from: string;
  to: string;
  bucket?: TimeseriesBucket;
  limit?: number;
}

export interface ModlistDiffParams {
  from_observed_at?: string;
  to_observed_at?: string;
  from_snapshot_id?: number;
  to_snapshot_id?: number;
}

export interface RecentUpdatesParams {
  since_hours?: number;
  limit?: number;
  sort?: string;
}

export interface DownloadGrowthParams {
  window_days?: number;
  limit?: number;
  sort?: string;
}

export interface EmergingParams {
  first_seen_within_days?: number;
  min_delta_downloads?: number;
  limit?: number;
  sort?: string;
}

export interface ChangelogParams {
  limit?: number;
  full?: boolean;
}

export interface FileMetricPointsParams {
  limit?: number;
}

// ── Release Type Helpers ────────────────────────────────────────────────────

export const CF_RELEASE_TYPES: Record<number, { label: string; color: string }> = {
  1: { label: 'Release', color: 'bg-green-500/20 text-green-300 border-green-500/30' },
  2: { label: 'Beta', color: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30' },
  3: { label: 'Alpha', color: 'bg-red-500/20 text-red-300 border-red-500/30' },
};
